import { lazy } from 'react'

import { useRouteMatch } from 'react-router'

import { Page } from '@webapp/core/components/Page'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { vendorReportConfig } from '@webapp/modules/reports/config/reportConfigs'
import { useReportVendorPresenter } from '@webapp/modules/reports/features/get-report-vendor/presenters/UseReportVendorPresenter'
import { useReportFilterCarryOver } from '@webapp/modules/reports/hooks/useReportFilterCarryOver'
import { type ReportDetailsPageParams } from '@webapp/modules/reports/pages/types'

const ReportDetailViewChunk = lazy(() =>
  import('@webapp/modules/reports/feature-sets/ReportDetailView').then(
    ({ ReportDetailView }) => ({
      default: ReportDetailView,
    })
  )
)

export const OrdersByVendorReportDetailsPage = () => {
  const intl = useProcurifyIntl()
  const pageTitle = intl.formatMessage(vendorReportConfig.titleKey)
  const { url } = useAppRoutes()
  const { withReportFilters } = useReportFilterCarryOver()
  const match = useRouteMatch<ReportDetailsPageParams>()

  const entityId = Number(match.params.id)

  // Breadcrumb entity name resolved independently of the line-item fetch.
  // Same query key and staleTime as the Feature Set's header
  // hook, so its later mount reads the cached entity instead of refetching.
  // An unknown id ends the trail in the not-found copy the FS shows.
  const {
    data: { vendorName },
    states: { isNotFound },
  } = useReportVendorPresenter({ vendorId: entityId })

  const entityCrumbName = isNotFound
    ? intl.formatMessage(vendorReportConfig.detailNotFoundHeaderKey)
    : (vendorName ?? '')

  return (
    <Page
      pageTitle={pageTitle}
      breadcrumbs={[
        {
          link: withReportFilters(url('OrdersByVendorReportPage')),
          name: pageTitle,
        },
        { name: entityCrumbName, escapeCaseFormatting: true },
      ]}
    >
      <ReportDetailViewChunk
        reportConfig={vendorReportConfig}
        entityId={entityId}
      />
    </Page>
  )
}
